/**
 * FirstDayBanner — celebratory "Day 1 of the school year" hero.
 *
 * Reads the in-person cohort start_date from `/api/academy/day-range` (cohort
 * config). Renders a full-width WOW banner on Day 1, becomes a compact
 * "Season Day N" strip after Day 1, and disappears entirely before Day 1.
 *
 * Zero manual date logic in the component — the cohort config is the source
 * of truth. Optional dismissal is deliberately NOT included on Day 1: this is
 * meant to greet every scholar who signs in today.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, Sparkles, Rocket, ChevronRight } from 'lucide-react';
import api from '../services/api';

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const FirstDayBanner = ({ studentName = 'Scholar', onScrollToWork }) => {
  const [cohort, setCohort] = useState(null);

  useEffect(() => {
    // Cohort meta is publicly readable — same-day banner for every scholar.
    api.get('/academy/in-person-cohort')
      .then((r) => setCohort(r.data.cohort))
      .catch(() => setCohort({ start_date: '2026-09-08', end_date: '2027-06-11' }));
  }, []);

  const state = useMemo(() => {
    if (!cohort?.start_date) return { visible: false };
    const today = toISO(new Date());
    if (today < cohort.start_date) return { visible: false };
    // Compute weekday count from start
    let day = 0;
    let cur = fromISO(cohort.start_date);
    const end = fromISO(today);
    while (cur <= end) {
      if (cur.getDay() >= 1 && cur.getDay() <= 5) day += 1;
      cur = new Date(cur.getTime() + 86400000);
    }
    // Count total school days
    let total = 0;
    let c = fromISO(cohort.start_date);
    const e = fromISO(cohort.end_date);
    while (c <= e) {
      if (c.getDay() >= 1 && c.getDay() <= 5) total += 1;
      c = new Date(c.getTime() + 86400000);
    }
    return { visible: true, day, total, isFirstDay: today === cohort.start_date };
  }, [cohort]);

  if (!state.visible) return null;

  if (state.isFirstDay) {
    return (
      <div
        className="mb-6 relative overflow-hidden border-2 border-orange-500 bg-gradient-to-br from-orange-500/20 via-orange-500/5 to-transparent"
        data-testid="first-day-banner"
      >
        {/* confetti-ish accent dots */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-3 left-6 w-1.5 h-1.5 bg-orange-400 animate-ping" />
          <div className="absolute top-8 right-12 w-1 h-1 bg-yellow-300 animate-pulse" />
          <div className="absolute bottom-6 left-14 w-1.5 h-1.5 bg-emerald-400 animate-pulse" />
          <div className="absolute bottom-12 right-8 w-1 h-1 bg-cyan-300 animate-ping" />
          <div className="absolute top-14 left-1/3 w-1 h-1 bg-pink-400 animate-pulse" />
        </div>
        <div className="relative px-5 py-6 sm:px-8 sm:py-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span className="text-[10px] tracking-[0.4em] font-black uppercase text-orange-400">
              First Day of School · September 8, 2026
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-[1.02] tracking-tight text-white break-words">
            Welcome to the Season,{' '}
            <span className="text-orange-500">{(studentName || 'Scholar').split(' ')[0]}.</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-zinc-200 leading-relaxed max-w-2xl">
            Day <span className="font-black text-orange-400">1</span> of{' '}
            <span className="font-black text-white">{state.total}</span>. The whistle blew.
            Class starts <span className="font-black text-white">12:30 PM PT</span>. Everything you need
            is stacked on this dashboard — top to bottom in the order you'll hit it.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={onScrollToWork}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-5 py-3 text-[11px] font-black uppercase tracking-[0.2em] transition-colors"
              data-testid="first-day-start-btn"
            >
              <Rocket className="w-4 h-4" /> Start Today's Work
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="inline-flex items-center gap-2 border-2 border-orange-500/40 px-4 py-2.5 text-[10px] uppercase tracking-widest font-black text-orange-300">
              <Trophy className="w-3 h-3" /> Day-1 badge unlocks tonight
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact strip after Day 1
  return (
    <div
      className="mb-5 border-2 border-emerald-500/40 bg-emerald-500/5 px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="season-day-strip"
    >
      <div className="w-9 h-9 bg-emerald-500 flex items-center justify-center flex-shrink-0">
        <Rocket className="w-4 h-4 text-black" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400">
          The Season Is On
        </div>
        <div className="text-white text-sm font-bold">
          Day {state.day} of {state.total} · Keep the streak alive
        </div>
      </div>
    </div>
  );
};

export default FirstDayBanner;
